/**
 * 🤖 AI Prompt Popup
 * 
 * Popup für zusätzliche Prompt-Informationen vor der AI-Generierung
 * - User kann spezifische Anweisungen geben
 * - Zeigt Context-Info an
 * - Submit/Cancel Buttons
 */

import React, { useState, useEffect, useRef } from 'react';
import './AIPromptPopup.css';

function AIPromptPopup({ 
    isOpen, 
    onClose, 
    onSubmit, 
    widgetType = 'Widget',
    contextInfo = '',
    isLoading = false
}) {
    const [promptText, setPromptText] = useState('');
    const [mode, setMode] = useState('improve'); // 'improve' or 'replace'
    const textareaRef = useRef(null);
    
    // Auto-focus textarea when popup opens
    useEffect(() => {
        if (isOpen && textareaRef.current) {
            textareaRef.current.focus();
        }
    }, [isOpen]);
    
    // Reset prompt when popup closes
    useEffect(() => {
        if (!isOpen) {
            setPromptText('');
            setMode('improve'); // Reset to default mode
        }
    }, [isOpen]);
    
    const handleSubmit = (e) => {
        e.preventDefault();
        if (onSubmit && !isLoading) {
            onSubmit({
                prompt: promptText.trim(),
                mode: mode
            });
        }
    };
    
    const handleKeyDown = (e) => {
        // Submit on Ctrl/Cmd + Enter
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            handleSubmit(e);
        }
        // Close on Escape
        if (e.key === 'Escape' && !isLoading) {
            onClose();
        }
    };
    
    if (!isOpen) return null;
    
    return (
        <div className="ai-prompt-popup-overlay" onClick={onClose}>
            <div className="ai-prompt-popup" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="ai-prompt-header">
                    <div className="ai-prompt-title">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M10 2L12.5 7.5L18 8.5L14 13L15 18.5L10 15.5L5 18.5L6 13L2 8.5L7.5 7.5L10 2Z" 
                                  stroke="currentColor" 
                                  strokeWidth="1.5" 
                                  fill="none"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"/>
                        </svg>
                        <span>Generate Content with AI</span>
                    </div>
                    {!isLoading && (
                        <button 
                            className="ai-prompt-close"
                            onClick={onClose}
                            title="Close (Esc)"
                        >
                            <i className="fas fa-times"></i>
                        </button>
                    )}
                </div>
                
                {/* Context Info */}
                {contextInfo && (
                    <div className="ai-prompt-context">
                        <div className="ai-prompt-context-label">
                            <i className="fas fa-info-circle"></i>
                            <span>Your Business Context</span>
                        </div>
                        <div className="ai-prompt-context-text">
                            {contextInfo.length > 200 
                                ? contextInfo.substring(0, 200) + '...' 
                                : contextInfo}
                        </div>
                    </div>
                )}
                
                {/* Form */}
                <form onSubmit={handleSubmit}>
                    <div className="ai-prompt-body">
                        {/* Mode Toggle */}
                        <div className="ai-prompt-mode-toggle">
                            <button
                                type="button"
                                className={`ai-mode-btn ${mode === 'improve' ? 'active' : ''}`}
                                onClick={() => setMode('improve')}
                                disabled={isLoading}
                            >
                                <i className="fas fa-magic"></i>
                                <div className="ai-mode-info">
                                    <span className="ai-mode-title">Improve Text</span>
                                    <span className="ai-mode-desc">Optimiert bestehenden Content (Styling bleibt)</span>
                                </div>
                            </button>
                            <button
                                type="button"
                                className={`ai-mode-btn ${mode === 'replace' ? 'active' : ''}`}
                                onClick={() => setMode('replace')}
                                disabled={isLoading}
                            >
                                <i className="fas fa-sync-alt"></i>
                                <div className="ai-mode-info">
                                    <span className="ai-mode-title">Replace Text</span>
                                    <span className="ai-mode-desc">Komplett neuer Text (Styling bleibt)</span>
                                </div>
                            </button>
                        </div>
                        
                        <label className="ai-prompt-label">
                            <i className="fas fa-lightbulb"></i>
                            <span>
                                {mode === 'improve' 
                                    ? 'How to improve? (Optional)' 
                                    : 'What should the new content be about? (Required)'}
                            </span>
                        </label>
                        <textarea
                            ref={textareaRef}
                            className="ai-prompt-textarea"
                            value={promptText}
                            onChange={(e) => setPromptText(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={mode === 'improve' 
                                ? "e.g., 'Make it more professional' or 'Add focus on sustainability' or 'Use emotional language'"
                                : "e.g., 'Write about organic wellness retreats' or 'Focus on luxury spa experiences' or 'Yoga and meditation benefits'"}
                            rows={5}
                            disabled={isLoading}
                        />
                        <div className="ai-prompt-hint">
                            <i className="fas fa-keyboard"></i>
                            <span>Tip: Press Ctrl+Enter to generate</span>
                        </div>
                        
                        {/* Styling Info Box */}
                        <div className="ai-prompt-info-box">
                            <i className="fas fa-shield-check"></i>
                            <div className="ai-prompt-info-text">
                                <strong>Styling bleibt erhalten:</strong> Font Size, Color, Weight, Margins und alle anderen Styling-Einstellungen werden NICHT verändert. Nur der Text-Inhalt wird {mode === 'improve' ? 'verbessert' : 'ersetzt'}.
                            </div>
                        </div>
                    </div>
                    
                    {/* Footer Buttons */}
                    <div className="ai-prompt-footer">
                        <button
                            type="button"
                            className="ai-prompt-btn ai-prompt-btn-cancel"
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="ai-prompt-btn ai-prompt-btn-submit"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <i className="fas fa-spinner fa-spin"></i>
                                    <span>Generating...</span>
                                </>
                            ) : (
                                <>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M8 2L10 6.5L14.5 7.5L11 11.5L12 16L8 13.5L4 16L5 11.5L1.5 7.5L6 6.5L8 2Z" 
                                              fill="currentColor"/>
                                    </svg>
                                    <span>Generate Content</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default AIPromptPopup;

